import { useState } from "react";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router";
import { CheckCircleIcon, NoSymbolIcon, PencilIcon } from "@heroicons/react/24/outline";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { DataTable } from "primereact/datatable";
import { useAppSelector } from "../../app/hooks";
import { isGUID } from "../../app/utils";
import { isHigherThanDistrictAdmin, UserRole } from "../../app/userSlice";
import { ClassLinkOrCleverIconWithTooltip } from "../../components/ClassLinkOrCleverIcon";
import { AvatarWithName } from "../../components/Avatar";
import { roleColors, UserListViewModel } from "./usersApiSlice";

interface PromptListTableProps {
	users: UserListViewModel[];
	handlePageChange: (page: number) => void;
}

export default function UsersListTable(props: PromptListTableProps) {
	const { role } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);
	const hasClassLinkOrCleverUser = props.users.some((u) => u.isClassLinkUser || u.isCleverUser);

	const [selectedUsers, setSelectedUsers] = useState<UserListViewModel[]>([]);

	const navigate = useNavigate();

	const checkboxBodyTemplate = (row: UserListViewModel) => {
		if (!row.canBeDeleted) return null;

		const isSelected = selectedUsers?.some((p) => p.userId === row.userId);

		return (
			<div className="flex items-center justify-center h-full w-full leading-none">
				<Checkbox
					checked={isSelected}
					onChange={(e) => {
						if (e.checked) {
							setSelectedUsers((prev) => [...prev, row]);
						} else {
							setSelectedUsers((prev) => prev.filter((u) => u.userId !== row.userId));
						}
					}}
					pt={{
						root: {
							className: "!size-[18px]",
						},
						box: {
							className: `!size-[18px] !rounded-sm !border ${isSelected ? "!bg-primary-600 !border-primary-600 p-highlight" : "bg-white"}`,
						},
						icon: { className: "!size-3" },
					}}
				/>
			</div>
		);
	};

	const enabledBodyTemplate = (row: UserListViewModel) => {
		return (
			<span className="flex justify-center">
				{row.accountStatus === "Enabled" ? (
					<CheckCircleIcon
						className="size-6 text-green-500"
						data-tooltip-id={`enabled-${row.userId}-tooltip`}
					/>
				) : (
					<NoSymbolIcon
						className="size-6 text-red-500"
						data-tooltip-id={`enabled-${row.userId}-tooltip`}
					/>
				)}
				<Tooltip
					id={`enabled-${row.userId}-tooltip`}
					place="top-start"
					content={row.accountStatus}
				/>
			</span>
		);
	};

	const nameBodyTemplate = (row: UserListViewModel) => {
		return (
			<AvatarWithName
				userId={Number(row.userId)}
				name={row.fullName}
				initials={row.initials}
				hasImage={row.hasImage}
			/>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: UserListViewModel) => {
		const tooltipContent = row.isCleverUser
			? "This user has a Clever account"
			: row.isClassLinkUser
				? "This user has a ClassLink account"
				: undefined;

		return (
			<ClassLinkOrCleverIconWithTooltip
				isClassLinkEntity={!!row.isClassLinkUser}
				isCleverEntity={!!row.isCleverUser}
				tooltipContent={tooltipContent}
				tooltipId={`outside-site-icon-${row.userId}-tooltip`}
			/>
		);
	};

	const usernameBodyTemplate = (row: UserListViewModel) => {
		const isGuidUsername = isGUID(row.userName);
		return (
			<div>
				<span data-tooltip-id={`username-${row.userId}-tooltip`}>
					{row.isClassLinkUser && isGuidUsername
						? "ClassLink User"
						: row.isCleverUser && isGuidUsername
							? "Clever User"
							: row.userName}
				</span>
				{isGuidUsername && (
					<Tooltip
						id={`username-${row.userId}-tooltip`}
						place="top-start"
						content={row.userName}
					/>
				)}
			</div>
		);
	};

	const roleBodyTemplate = (row: UserListViewModel) => {
		return (
			<div className="flex justify-center">
				<span
					className={`${roleColors[row.role as UserRole]} flex w-28 shrink-0 items-center justify-center rounded-full px-2 py-1`}
					data-tooltip-id={`role-${row.userId}-tooltip`}>
					{row.role === UserRole.Admin ? (
						<span aria-hidden="true" className="text-sm">
							Super
						</span>
					) : row.role === UserRole.StateAdmin ? (
						<span aria-hidden="true" className="text-sm">
							State
						</span>
					) : row.role === UserRole.DistrictAdmin ? (
						<span aria-hidden="true" className="text-sm">
							District
						</span>
					) : row.role === UserRole.SchoolAdmin ? (
						<span aria-hidden="true" className="text-sm">
							School
						</span>
					) : row.role === UserRole.Proctor ? (
						<span aria-hidden="true" className="text-sm">
							Teacher
						</span>
					) : row.isTeachersWriterAccount ? (
						<span aria-hidden="true" className="text-sm text-center">
							Student (Teacher)
						</span>
					) : row.role === UserRole.Writer ? (
						<span aria-hidden="true" className="text-sm">
							Student
						</span>
					) : (
						<span aria-hidden="true" className="text-sm">
							Independent
						</span>
					)}
					<Tooltip
						id={`role-${row.userId}-tooltip`}
						place="top-start"
						content={row.roleDescription}
					/>
				</span>
			</div>
		);
	};

	const schoolsBodyTemplate = (row: UserListViewModel) => {
		return <div className="line-clamp-1 hover:line-clamp-none">{row.school}</div>;
	};

	const actionBodyTemplate = (row: UserListViewModel) => {
		return row.canBeImpersonated ? (
			<button
				id={`impersonate-${row.userId}`}
				className="btn btn-action !px-2"
				onClick={() => {}}>
				Sign In
			</button>
		) : (
			<></>
		);
	};

	const editBodyTemplate = (row: UserListViewModel) => {
		return (
			<button
				id={`edit-${row.userId}-button`}
				className="mr-2 cursor-pointer"
				onClick={() => navigate(`/users/edit?userId=${row.userId}`)}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	return (
		<DataTable
			value={props.users}
			dataKey="userId"
			selection={selectedUsers}
			selectionMode="checkbox"
			stripedRows
			emptyMessage="No users found"
			className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
			style={{ paddingTop: "16px" }}>
			{role && isHigherThanDistrictAdmin(role) && (
				<Column
					headerClassName="!pr-2"
					className="!pr-2 !align-middle"
					body={checkboxBodyTemplate}
					columnKey={`selection-${selectedUsers.length}`}
				/>
			)}
			<Column
				header="Status"
				body={enabledBodyTemplate}
				headerClassName="!px-2 xl:!pr-4 max-w-14"
				className="!px-2 xl:!pr-4 max-w-14"></Column>
			<Column
				header="Name"
				body={nameBodyTemplate}
				headerClassName="!px-2"
				className="!px-0.5 xl:!px-2"></Column>
			{!config?.isUtahComposeSite && hasClassLinkOrCleverUser && (
				<Column
					body={cleverOrClassLinkBodyTemplate}
					headerClassName="hidden xl:table-cell !px-0 w-4"
					className="hidden xl:table-cell !px-0"></Column>
			)}
			<Column
				header="Username"
				body={usernameBodyTemplate}
				headerClassName="!px-0.5 xl:!px-2"
				className="break-all xl:break-normal !px-1 xl:!px-2"></Column>
			<Column
				header="External ID"
				field="externalId"
				headerClassName="!px-1 xl:!px-2 xl:min-w-24"
				className="!px-1 xl:!px-2 xl:min-w-24"></Column>
			<Column
				header="Role"
				body={roleBodyTemplate}
				headerClassName="!px-2 xl:!px-4"
				className="!px-1 xl:!px-2"></Column>
			<Column
				header="Schools"
				body={schoolsBodyTemplate}
				headerClassName="!px-1 xl:!px-2"
				className="!px-1 xl:!px-2 max-w-64"></Column>
			<Column
				header="Last Login Date"
				field="loginAt"
				headerClassName="!px-1 xl:!px-2 max-w-12"
				className="!px-1 xl:!px-2 xl:min-w-52"
			/>
			{role && isHigherThanDistrictAdmin(role) && (
				<Column
					body={actionBodyTemplate}
					headerClassName="!px-2 w-24"
					className="!px-2"></Column>
			)}
			<Column body={editBodyTemplate} className="!px-1 xl:!pr-2"></Column>
		</DataTable>
	);
}
